import {
  AfterViewInit,
  Component,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { ConfirmationService } from "primeng/api";
import { Router, ActivatedRoute, RouterModule } from "@angular/router";
import { PARAM_STARTUPS_PATH } from "src/app/admin/constants/constants";
import { AbstractListController } from "../../common/abstract-list.component";
import { Startups } from "src/app/models/startups";
import { StartupsService } from "src/app/services/startups.service";
import { CommonModule } from "@angular/common";
import { TagModule } from "primeng/tag";
import { DataTableComponent } from "../../common/data-table/data-table.component";
import {
  RowAction,
  TableColumn,
  TableFilter,
} from "../../common/data-table/data-table.model";

@Component({
  standalone: true,
  selector: "app-manage-startups",
  templateUrl: "./manage-startups.component.html",
  imports: [CommonModule, RouterModule, TagModule, DataTableComponent],
})
export class ManageStartupsComponent
  extends AbstractListController<Startups, any>
  implements AfterViewInit
{
  title = "Startups";

  @ViewChild("imageTpl") imageTpl!: TemplateRef<any>;
  @ViewChild("nameTpl") nameTpl!: TemplateRef<any>;
  @ViewChild("statusTpl") statusTpl!: TemplateRef<any>;
  @ViewChild("backlinkTpl") backlinkTpl!: TemplateRef<any>;
  @ViewChild("drTpl") drTpl!: TemplateRef<any>;

  domainRatings: {
    [id: number]: { loading: boolean; rating?: number; error?: boolean };
  } = {};

  tableColumns: TableColumn[] = [
    { field: "image", header: "Image", type: "custom", width: "60px" },
    { field: "startup_name", header: "Name", type: "custom" },
    { field: "points", header: "Points", width: "80px" },
    { field: "status", header: "Sta", type: "custom", width: "70px" },
    { field: "backlink", header: "BaL", type: "custom", width: "60px" },
    { field: "page_url", header: "Page Url" },
    { field: "dr", header: "DR", type: "custom", width: "70px" },
    { field: "publish_date", header: "Date" },
  ];

  tableFilters: TableFilter[] = [
    { field: "startup_name", header: "Startup Name", type: "string" },
    { field: "page_url", header: "Slug", type: "string" },
    {
      field: "status",
      header: "Status",
      type: "select",
      options: [
        { label: "Active", value: "1" },
        { label: "Not active", value: "0" },
      ],
    },
    { field: "publish_date_from", header: "Published from", type: "date" },
    { field: "publish_date_to", header: "Published to", type: "date" },
    {
      field: "is_featured",
      header: "Featured",
      type: "select",
      options: [
        { label: "Featured", value: "1" },
        { label: "Not featured", value: "0" },
      ],
    },
  ];

  rowActions: RowAction[] = [
    {
      icon: "pi pi-eye",
      label: "edit",
      styleClass: "p-button p-button-primary",
      link: (row) => ["/admin/startups/edit", row.id],
    },
    {
      icon: "pi pi-external-link",
      label: "website",
      styleClass: "p-button p-button-outline",
      event: "website",
    },
  ];

  constructor(
    private startupsService: StartupsService,
    protected confirmationService: ConfirmationService,
    router: Router,
    route: ActivatedRoute,
  ) {
    super(
      startupsService,
      confirmationService,
      router,
      route,
      PARAM_STARTUPS_PATH,
    );
    this.useTableFilters = true;
  }

  ngAfterViewInit(): void {
    const map: Record<string, TemplateRef<any>> = {
      image: this.imageTpl,
      startup_name: this.nameTpl,
      status: this.statusTpl,
      backlink: this.backlinkTpl,
      dr: this.drTpl,
    };
    this.tableColumns.forEach((col) => {
      if (map[col.field]) {
        col.template = map[col.field];
      }
    });
    this.cdr.detectChanges();
  }

  onAction(e: { event: string; row: Startups }): void {
    if (e.event === "website") {
      this.goToUrl(e.row.startup_url);
    }
  }

  goToUrl(startup_url: any): void {
    window.open(startup_url, "_blank");
  }

  protected override afterLoad(): void {
    (this.items || []).forEach((startup) => this.checkDomainRating(startup));
  }

  checkDomainRating(startup: Startups): void {
    const domain = this.startupsService.extractDomain(startup.startup_url);
    if (!domain) {
      this.domainRatings[startup.id] = { loading: false, error: true };
      return;
    }
    this.domainRatings[startup.id] = { loading: true };
    this.startupsService.checkDomainRating(domain).subscribe({
      next: (res) => {
        this.domainRatings[startup.id] = {
          loading: false,
          rating: res?.data?.domain_rating,
        };
        this.cdr.detectChanges();
      },
      error: () => {
        this.domainRatings[startup.id] = { loading: false, error: true };
        this.cdr.detectChanges();
      },
    });
  }

  getDomainRankGaugeBackground(rating: number): string {
    const pct = Math.max(0, Math.min(100, rating));
    return (
      `conic-gradient(transparent 0 ${pct}%, #eee ${pct}% 100%),` +
      `conic-gradient(#ff3b30 0%, #ff9500 25%, #ffcc00 50%, #90ee90 75%, #34c759 100%)`
    );
  }
}
